import { READONLY_APP_ROLES, type AppRole } from "@/hooks/accessRoleUtils";
import type { OrgRole } from "@/hooks/useAccessContext";

/**
 * Who may read and change Accounts payable settings (`finance_ap_settings`), decided from the
 * roles the app has already loaded. One decision for every surface that offers the page, so
 * PayablesSettings' own gate and the T5 settings nav item cannot drift apart. Display gating
 * only: RLS stays the authority, and this never grants anything the server would refuse.
 *
 * Every policy on the table goes through `can_access_finance_org(org_id, roles)`
 * (20260622150000_finance_org_scope_role_check.sql): the viewer must be a member of the
 * organization, and hold one of the roles in it or globally. Those are `orgRole` and
 * `orgAppRolesDbOnly` from usePermissions. The read-only block on writes is wider for one role
 * (see `canEditPayablesSettings`), which is why `appRolesDbOnly` is here too.
 */
export interface PayablesSettingsViewer {
  /** The organization_members role in the active organization; null when not a member. */
  orgRole: OrgRole | null | undefined;
  /** user_roles rows in the active organization plus global (null-org) ones. */
  orgAppRolesDbOnly: readonly AppRole[] | null | undefined;
  /** Every user_roles row the viewer holds, in any organization (usePermissions' `appRolesDbOnly`). */
  appRolesDbOnly: readonly AppRole[] | null | undefined;
}

/**
 * The write policies (insert and update; the save is an upsert), in
 * 20260615210000_rls_normalize_multiple_permissive.sql.
 */
export const AP_SETTINGS_WRITE_ROLES: readonly AppRole[] = [
  "admin",
  "super_admin",
  "finance",
  "finance_admin",
  "bookkeeper",
  "approver",
];

/**
 * The SELECT policy: the write roles, then `can_access_finance_org(org_id)`'s default list (the
 * platform roles), in 20260615210000_rls_normalize_multiple_permissive.sql; and `auditor`, whose
 * read 20260626210000_finance_rbac_complete_audit_personas.sql adds to every table guarded by
 * `can_access_finance_org` (20260630030000_rls_normalize_recent_policy_overlays.sql folds it into
 * the same policy).
 */
export const AP_SETTINGS_READ_ROLES: readonly AppRole[] = [
  ...AP_SETTINGS_WRITE_ROLES,
  "platform_super_admin",
  "platform_support",
  "platform_readonly",
  "auditor",
];

/**
 * The read-only role `is_readonly_app_user_for_org` matches whichever organization its row names
 * (20260803222239_readonly_write_authority.sql, last redefined in
 * 20260921230000_tombstone_write_gate_starts_when_deletion_starts.sql). Its other read-only role,
 * auditor, counts only for this organization or globally.
 */
const READONLY_IN_EVERY_ORG: readonly AppRole[] = ["platform_readonly"];

function holdsAny(held: readonly AppRole[] | null | undefined, roles: readonly AppRole[]): boolean {
  return (held ?? []).some((role) => roles.includes(role));
}

/**
 * Whether RLS lets the viewer read this organization's settings. A refused read returns no row,
 * not an error, and the API reads no row as ON, so a viewer this refuses must not be shown a value.
 */
export function canReadPayablesSettings(viewer: PayablesSettingsViewer): boolean {
  return Boolean(viewer.orgRole) && holdsAny(viewer.orgAppRolesDbOnly, AP_SETTINGS_READ_ROLES);
}

/**
 * Whether RLS lets the viewer change them: a write role, and no read-only role. The restrictive
 * readonly_roles_block_* policies refuse any write by an auditor of this organization (or a global
 * one), or by a platform_readonly user, whatever else they hold. platform_readonly counts from
 * every organization, so a finance writer here with a platform_readonly row for another
 * organization is still read-only here.
 */
export function canEditPayablesSettings(viewer: PayablesSettingsViewer): boolean {
  return (
    canReadPayablesSettings(viewer)
    && holdsAny(viewer.orgAppRolesDbOnly, AP_SETTINGS_WRITE_ROLES)
    && !holdsAny(viewer.orgAppRolesDbOnly, READONLY_APP_ROLES)
    && !holdsAny(viewer.appRolesDbOnly, READONLY_IN_EVERY_ORG)
  );
}
